/**
 * Grava um áudio no navegador para mandar como mensagem de voz.
 *
 * Fases: "parado" → "pedindo" (o navegador pergunta do microfone) →
 * "gravando" (cronômetro) → "pronto" (dá para ouvir antes de mandar).
 * Grava no formato que o navegador tiver (WebM ou OGG com Opus, ou MP4 no
 * Safari); o monitor converte para OGG/Opus. Para sozinho em 15 minutos.
 * Fechar a conversa no meio solta o microfone e descarta.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { TAMANHO_MAXIMO_DO_ENVIO, formatoDeAudio, tamanhoLegivel } from "../apresentacao";

export const LIMITE_DA_GRAVACAO_S = 15 * 60;
const PASSO_DO_CRONOMETRO_MS = 250;
const FATIA_DA_GRAVACAO_MS = 1000;

function nomeDoArquivo(extensao) {
  const agora = new Date();
  const dois = (n) => String(n).padStart(2, "0");
  const carimbo = `${agora.getFullYear()}${dois(agora.getMonth() + 1)}${dois(agora.getDate())}-${dois(agora.getHours())}${dois(agora.getMinutes())}${dois(agora.getSeconds())}`;
  return `audio-${carimbo}.${extensao}`;
}

export default function useGravadorDeAudio() {
  const [fase, setFase] = useState("parado");
  const [segundos, setSegundos] = useState(0);
  const [gravacao, setGravacao] = useState(null);
  const [erro, setErro] = useState("");
  const gravador = useRef(null);
  const pedacos = useRef([]);
  const descartarAoParar = useRef(false);
  const inicio = useRef(0);
  const relogio = useRef(null);
  const endereco = useRef("");

  const pararRelogio = () => {
    clearInterval(relogio.current);
    relogio.current = null;
  };

  const soltarEndereco = () => {
    if (endereco.current) URL.revokeObjectURL(endereco.current);
    endereco.current = "";
  };

  const parar = useCallback(() => {
    if (gravador.current && gravador.current.state !== "inactive") gravador.current.stop();
  }, []);

  const iniciar = useCallback(async () => {
    setErro("");
    const Gravador = typeof window === "undefined" ? undefined : window.MediaRecorder;
    const formato = formatoDeAudio(Gravador);
    if (!formato || !navigator.mediaDevices?.getUserMedia) {
      setErro("Este navegador não grava áudio. Use o Chrome, o Edge ou o Safari atualizado.");
      return;
    }
    setFase("pedindo");
    let fluxo;
    try {
      fluxo = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setFase("parado");
      setErro("Sem acesso ao microfone. Libere o microfone para este site no navegador.");
      return;
    }
    const tipo = formato.mime.split(";")[0];
    const novo = new Gravador(fluxo, { mimeType: formato.mime });
    pedacos.current = [];
    descartarAoParar.current = false;
    novo.ondataavailable = (evento) => {
      if (evento.data?.size) pedacos.current.push(evento.data);
    };
    novo.onstop = () => {
      fluxo.getTracks().forEach((faixa) => faixa.stop());
      pararRelogio();
      gravador.current = null;
      if (descartarAoParar.current || !pedacos.current.length) {
        setFase("parado");
        setSegundos(0);
        return;
      }
      const duracao = Math.max(1, Math.round((Date.now() - inicio.current) / 1000));
      const arquivo = new File(pedacos.current, nomeDoArquivo(formato.extensao), { type: tipo });
      if (arquivo.size > TAMANHO_MAXIMO_DO_ENVIO) {
        setFase("parado");
        setErro(`O áudio ficou com ${tamanhoLegivel(arquivo.size)}; o limite é 16 MB. Grave em partes menores.`);
        return;
      }
      soltarEndereco();
      endereco.current = URL.createObjectURL(arquivo);
      setGravacao({ arquivo, url: endereco.current, duracao });
      setFase("pronto");
    };
    gravador.current = novo;
    inicio.current = Date.now();
    setSegundos(0);
    novo.start(FATIA_DA_GRAVACAO_MS);
    setFase("gravando");
    relogio.current = setInterval(() => {
      const passados = Math.floor((Date.now() - inicio.current) / 1000);
      setSegundos(passados);
      if (passados >= LIMITE_DA_GRAVACAO_S) parar();
    }, PASSO_DO_CRONOMETRO_MS);
  }, [parar]);

  /** Descarta: no meio da gravação (solta o microfone) ou depois, antes de mandar. */
  const descartar = useCallback(() => {
    if (gravador.current && gravador.current.state !== "inactive") {
      descartarAoParar.current = true;
      gravador.current.stop();
      return;
    }
    soltarEndereco();
    setGravacao(null);
    setSegundos(0);
    setFase("parado");
  }, []);

  /** Depois de mandar: o arquivo segue com o balão (para tentar de novo); a tela volta ao normal. */
  const entregar = useCallback(() => {
    soltarEndereco();
    setGravacao(null);
    setSegundos(0);
    setFase("parado");
  }, []);

  useEffect(
    () => () => {
      descartarAoParar.current = true;
      if (gravador.current && gravador.current.state !== "inactive") gravador.current.stop();
      pararRelogio();
      soltarEndereco();
    },
    [],
  );

  return { fase, segundos, gravacao, erro, limparErro: () => setErro(""), iniciar, parar, descartar, entregar };
}
